import { splitCellTotals, ViaColumn } from './bulkOffersColumns';

export const BULK_QUOTA_WARNING = {
  OFFER_QUOTA_EXCEEDED: 'OFFER_QUOTA_EXCEEDED',
  ACADEMIC_OFFER_QUOTA_EXCEEDED: 'ACADEMIC_OFFER_QUOTA_EXCEEDED',
} as const;

const ROW_KEY_SEPARATOR = '_';
const ROW_KEY_NO_VIA = 'null';

/**
 * Key that ties an upsert sent to `bulk-preview` back to the offer/via cell it
 * came from. The preview echoes it in every row result, which is how a warning
 * is attributed to an offer without widening the API contract.
 */
export function bulkRowKey(
  offerId: number,
  admissionViaId: string | null,
): string {
  return `${offerId}${ROW_KEY_SEPARATOR}${admissionViaId ?? ROW_KEY_NO_VIA}`;
}

/** Null for keys the editor did not build, such as the `delete:<id>` rows. */
export function offerIdFromRowKey(rowKey: string): number | null {
  const [head] = rowKey.split(ROW_KEY_SEPARATOR);
  const offerId = Number(head);
  return head !== '' && Number.isInteger(offerId) ? offerId : null;
}

export interface QuotaConflictPreviewRow {
  rowKey: string;
  warnings: string[];
}

export interface QuotaConflictFormRow {
  offerId: number;
  cells: ({ quota: string } | undefined)[];
}

export interface QuotaConflictInput {
  previewRows: QuotaConflictPreviewRow[];
  rows: QuotaConflictFormRow[];
  viaColumns: ViaColumn[];
  offerQuotaById: Map<number, number | null>;
  offerLabelById: Map<number, string>;
  academicOfferQuota: number | null;
}

/** An offer holding more seats than the quota declared for it. */
export interface OfferQuotaConflict {
  offerId: number;
  label: string;
  allocated: number;
  /** Null when the offer declares no quota of its own. */
  quota: number | null;
}

/** The process as a whole holding more seats than the academic offer's quota. */
export interface ProcessQuotaConflict {
  allocated: number;
  quota: number;
  /** Every offer contributing seats, so the overflow can be traced. */
  offers: { offerId: number; label: string; allocated: number }[];
}

export interface QuotaConflicts {
  offers: OfferQuotaConflict[];
  process: ProcessQuotaConflict | null;
}

/**
 * Turns the preview's per-row warnings into what the confirmation modal shows.
 *
 * Which conflicts exist comes from the preview — the API is the only thing that
 * decides whether the save is over quota. The figures come from the form, which
 * is what the admin just typed and what the table already displays. Both blocks
 * are independent: exceeding an offer's quota and exceeding the academic
 * offer's are different problems and can happen at once.
 */
export function deriveQuotaConflicts({
  previewRows,
  rows,
  viaColumns,
  offerQuotaById,
  offerLabelById,
  academicOfferQuota,
}: QuotaConflictInput): QuotaConflicts {
  const labelFor = (offerId: number) =>
    offerLabelById.get(offerId) ?? `#${offerId}`;

  // Separate-quota vias sit on top of the declared quota, so only the
  // deducting part of each row counts against it.
  const allocatedByOffer = new Map<number, number>();
  let totalAllocated = 0;
  rows.forEach((row) => {
    const { deducting } = splitCellTotals(row.cells ?? [], viaColumns);
    allocatedByOffer.set(
      row.offerId,
      (allocatedByOffer.get(row.offerId) ?? 0) + deducting,
    );
    totalAllocated += deducting;
  });

  const warnedOfferIds: number[] = [];
  let processWarned = false;
  previewRows.forEach((previewRow) => {
    const offerId = offerIdFromRowKey(previewRow.rowKey);
    previewRow.warnings.forEach((warning) => {
      if (warning === BULK_QUOTA_WARNING.ACADEMIC_OFFER_QUOTA_EXCEEDED) {
        processWarned = true;
        return;
      }
      // An offer is flagged once even if several of its vias carry the warning.
      if (
        warning === BULK_QUOTA_WARNING.OFFER_QUOTA_EXCEEDED &&
        offerId != null &&
        !warnedOfferIds.includes(offerId)
      ) {
        warnedOfferIds.push(offerId);
      }
    });
  });

  const offers = warnedOfferIds.map((offerId) => ({
    offerId,
    label: labelFor(offerId),
    allocated: allocatedByOffer.get(offerId) ?? 0,
    quota: offerQuotaById.get(offerId) ?? null,
  }));

  const process =
    processWarned && academicOfferQuota != null
      ? {
          allocated: totalAllocated,
          quota: academicOfferQuota,
          offers: rows
            .filter((row) => (allocatedByOffer.get(row.offerId) ?? 0) > 0)
            .map((row) => ({
              offerId: row.offerId,
              label: labelFor(row.offerId),
              allocated: allocatedByOffer.get(row.offerId) ?? 0,
            })),
        }
      : null;

  return { offers, process };
}
